
import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  schema?: object;
  /** Prevent the page from being indexed by search engines */
  noindex?: boolean;
}

/**
 * React hook that updates SEO related tags in the document head.
 *
 * It safely runs only in a browser environment and cleans up any
 * injected JSON‑LD script when the component unmounts or the schema
 * changes.
 */
export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  schema,
  noindex,
}: SEOProps) => {
  useEffect(() => {
    // Guard against server‑side rendering
    if (typeof document === 'undefined') {
      return;
    }

    /**
     * Ensure a <meta> tag exists.
     *
     * @param selector   CSS selector used to look for an existing tag.
     * @param attrName   Attribute name to set when creating a new tag (e.g. "name" or "property").
     * @param attrValue  Attribute value to set when creating a new tag.
     * @param createAttrs Optional extra attributes to set on a newly created tag.
     * @returns The existing or newly‑created meta element.
     */
    const ensureMeta = (
      selector: string,
      attrName: string,
      attrValue: string,
      createAttrs?: Record<string, string>
    ): HTMLMetaElement => {
      let meta = document.querySelector(selector) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue);
        if (createAttrs) {
          Object.entries(createAttrs).forEach(([k, v]) => {
            meta!.setAttribute(k, v);
          });
        }
        document.head.appendChild(meta);
      }
      return meta;
    };

    // ---- Title ------------------------------------------------------------
    if (title) {
      document.title = title;

      const ogTitle = document.querySelector(
        'meta[property="og:title"]'
      ) as HTMLMetaElement | null;
      const twitterTitle = document.querySelector(
        'meta[name="twitter:title"]'
      ) as HTMLMetaElement | null;

      ogTitle?.setAttribute('content', title);
      twitterTitle?.setAttribute('content', title);
    }

    // ---- Description ------------------------------------------------------
    if (description) {
      const metaDesc = ensureMeta('meta[name="description"]', 'name', 'description');
      metaDesc.setAttribute('content', description);

      const ogDesc = document.querySelector(
        'meta[property="og:description"]'
      ) as HTMLMetaElement | null;
      const twitterDesc = document.querySelector(
        'meta[name="twitter:description"]'
      ) as HTMLMetaElement | null;

      ogDesc?.setAttribute('content', description);
      twitterDesc?.setAttribute('content', description);
    }

    // ---- Keywords ---------------------------------------------------------
    if (keywords) {
      const metaKeywords = ensureMeta('meta[name="keywords"]', 'name', 'keywords');
      metaKeywords.setAttribute('content', keywords);
    }

    // ---- Robots (noindex) -------------------------------------------------
    if (noindex) {
      const metaRobots = ensureMeta('meta[name="robots"]', 'name', 'robots');
      metaRobots.setAttribute('content', 'noindex, nofollow');
    } else {
      // If we previously added a robots tag, remove it so the page can be indexed.
      const metaRobots = document.querySelector('meta[name="robots"]');
      metaRobots?.remove();
    }

    // ---- Canonical URL ----------------------------------------------------
    if (canonical) {
      let linkCanonical = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement | null;

      if (!linkCanonical) {
        linkCanonical = document.createElement('link');
        linkCanonical.setAttribute('rel', 'canonical');
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.href = canonical;

      const ogUrl = document.querySelector(
        'meta[property="og:url"]'
      ) as HTMLMetaElement | null;
      ogUrl?.setAttribute('content', canonical);
    }

    // ---- JSON‑LD Structured Data ------------------------------------------
    let schemaScript: HTMLScriptElement | null = null;
    if (schema) {
      // Remove any previous script that this hook generated
      const previous = document.querySelector(
        'script[data-using-seo="true"]'
      ) as HTMLScriptElement | null;
      previous?.remove();

      schemaScript = document.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.setAttribute('data-using-seo', 'true');
      // Wrap JSON.stringify in a try/catch to avoid breaking the hook
      try {
        schemaScript.text = JSON.stringify(schema);
      } catch (e) {
        console.error('Failed to serialize SEO schema:', e);
        schemaScript.text = '';
      }
      document.head.appendChild(schemaScript);
    }

    // Cleanup: remove the injected JSON‑LD when the component unmounts
    return () => {
      schemaScript?.remove();
    };
  }, [title, description, keywords, canonical, schema, noindex]);
};
